import type { MatchState } from '../match/MatchState';
import type { PlayerRuntimeState } from '../match/PlayerRuntimeState';
import { PITCH_GEOMETRY } from '../match/PitchGeometry';
import { add, clamp, clampPosition, distance, length, lerp, moveTowards, normalize, scale, subtract, type Vector2 } from '../match/Vector2';
import { SteeringSystem } from './SteeringSystem';
import { PassiveSystem } from './PassiveSystem';
import { TacticalRoleSystem } from './TacticalRoleSystem';

export class MovementSystem {
  constructor(
    private readonly steering = new SteeringSystem(),
    private readonly passives = new PassiveSystem(),
    private readonly roles = new TacticalRoleSystem(),
  ) {}

  update(state: MatchState, deltaTime: number): void {
    const activeDuelPlayers = this.activeDuelPlayers(state);
    const separation = this.separationVectors(state, activeDuelPlayers);
    for (let index = 0; index < state.players.length; index++) {
      const player = state.players[index];
      const speed = this.finalSpeed(state, player);
      const agility = Math.max(0, (player.stats.control * .65 + player.stats.speed * .35) / 100);
      const turnRate = state.config.minimumTurnRateRadiansPerSecond
        + (state.config.maximumTurnRateRadiansPerSecond - state.config.minimumTurnRateRadiansPerSecond) * agility;
      this.steering.update(
        player,
        deltaTime,
        speed,
        state.config.acceleration,
        separation[index],
        turnRate,
        state.config.brakingAccelerationMultiplier,
      );
      this.keepGoalkeeperInArea(player);
      player.actionCooldown = Math.max(0, player.actionCooldown - deltaTime);
      this.drainStamina(state, player, deltaTime);
    }
    this.resolveCrowding(state, deltaTime, activeDuelPlayers);
    this.updateBall(state, deltaTime);
  }

  finalSpeed(state: MatchState, player: PlayerRuntimeState): number {
    // A curved attribute mapping makes a visibly quick footballer pull away
    // from an average one instead of compressing the whole roster into a
    // nearly identical cruising speed.
      const speedRatio = Math.pow(Math.max(0, player.stats.speed / 100), state.config.speedAttributeExponent);
    const baseSpeed = state.config.minPlayerSpeed
      + (state.config.maxPlayerSpeed - state.config.minPlayerSpeed) * speedRatio;
    const ratio = player.staminaCurrent;
    const staminaModifier = ratio > 70 ? 1 : ratio > 40 ? 0.9 : ratio > 20 ? 0.78 : 0.65;
    const actionModifier = player.staminaCurrent < 10
      ? 0.6
      : ['PRESSING', 'TRACKING_RUN', 'MAKING_RUN', 'CHASING_LOOSE_BALL', 'RECOVERING'].includes(player.movementState)
        ? 1.2
        : player.movementState === 'HOLDING_SHAPE' || player.movementState === 'GOALKEEPER_POSITIONING' ? 0.75 : 1;
    const role = player.positionType === 'GK' ? null : player.tacticalProfile.assignedRole;
    const roleModifier = player.movementState === 'RECOVERING'
      ? this.roles.movementBias(role, 'recoveryBias')
      : player.movementState === 'MAKING_RUN' ? this.roles.movementBias(role, 'forwardRunBias')
        : player.movementState === 'PRESSING' ? this.roles.movementBias(role, 'pressBias')
          : player.movementState === 'SUPPORTING_ATTACK' ? this.roles.movementBias(role, 'supportBias')
            : player.movementState === 'HOLDING_SHAPE' ? this.roles.movementBias(role, 'holdShapeBias') : 1;
    const counterStartedAt = state.counterWindowByTeam[player.teamSide] - this.passives.config.counterWindowSeconds;
    const counterSpeed = state.possessionTeam === player.teamSide
      && state.time <= state.counterWindowByTeam[player.teamSide]
      && ['MAKING_RUN', 'SUPPORTING_ATTACK', 'CARRYING_BALL'].includes(player.movementState)
      && this.passives.isCounterWindowActive(player, state.time - counterStartedAt)
      ? this.passives.config.counterRunSpeedMultiplier
      : 1;
    const dribbleExitSpeed = state.ball.ownerPlayerId === player.playerId
      && (player.dribbleAdvantageUntil ?? -Infinity) > state.time ? 1.18 : 1;
    return baseSpeed * staminaModifier * actionModifier * roleModifier * counterSpeed * dribbleExitSpeed;
  }

  private drainStamina(state: MatchState, player: PlayerRuntimeState, deltaTime: number): void {
    const effort = ['PRESSING', 'TRACKING_RUN', 'MAKING_RUN', 'CHASING_LOOSE_BALL', 'RECOVERING'].includes(player.movementState) ? 0.012 : 0.004;
    const resistance = 0.55 + player.stats.stamina / 220;
    const furyDrain = state.fury[player.teamSide].active ? state.config.furyStaminaDrainMultiplier : 1;
    player.staminaCurrent = Math.max(0, player.staminaCurrent - effort * furyDrain * deltaTime / resistance);
  }

  private updateBall(state: MatchState, deltaTime: number): void {
    const ball = state.ball;
    if (ball.state === 'OWNED' && ball.ownerPlayerId) {
      const owner = state.players.find((player) => player.playerId === ball.ownerPlayerId);
      if (owner) {
        ball.position = { ...owner.position };
        ball.velocity = { ...owner.velocity };
        ball.height = 0;
      }
      return;
    }
    if (ball.state === 'OUT') return;
    if (ball.state === 'PASSING' || ball.state === 'SHOOTING') {
      const flight = ball.flight;
      if (flight) {
        const progress = clamp((state.time - flight.startedAt + deltaTime) / Math.max(.001, flight.duration), 0, 1);
        // A long distribution leaves the foot with a small burst, then loses
        // just enough apparent speed near the receiver for the run and the
        // first touch to read on screen. The physical arrival time remains
        // deterministic and proportional to distance.
        const travelProgress = ball.state === 'PASSING' && flight.duration >= 5
          ? clamp(progress + Math.sin(progress * Math.PI) * .07, 0, 1)
          : progress;
        const direct = {
          x: lerp(flight.origin.x, ball.targetPosition.x, travelProgress),
          y: lerp(flight.origin.y, ball.targetPosition.y, travelProgress),
        };
        const offset = subtract(ball.targetPosition, flight.origin);
        const routeLength = Math.max(.001, length(offset));
        const arcProgress = Math.sin(travelProgress * Math.PI);
        const curve = scale({ x: -offset.y / routeLength, y: offset.x / routeLength }, flight.curve * arcProgress);
        const nextPosition = { x: direct.x + curve.x, y: direct.y + curve.y };
        ball.velocity = scale(subtract(nextPosition, ball.position), 1 / Math.max(.001, deltaTime));
        ball.position = nextPosition;
        ball.height = flight.arcHeight * arcProgress;
        flight.progress = progress;
        if (progress >= 1) {
          ball.position = { ...ball.targetPosition };
          ball.velocity = { x: 0, y: 0 };
          ball.height = 0;
        }
        return;
      }
      const remaining = distance(ball.position, ball.targetPosition);
      const step = ball.speed * deltaTime;
      ball.velocity = scale(normalize(subtract(ball.targetPosition, ball.position)), ball.speed);
      ball.position = moveTowards(ball.position, ball.targetPosition, step);
      ball.height = 0;
      if (remaining <= step) ball.velocity = { x: 0, y: 0 };
      return;
    }
    ball.position = moveTowards(ball.position, {
      x: ball.position.x + ball.velocity.x,
      y: ball.position.y + ball.velocity.y,
    }, Math.hypot(ball.velocity.x, ball.velocity.y) * deltaTime);
    ball.velocity = scale(ball.velocity, Math.pow(state.config.looseBallFriction, deltaTime));
    ball.height = 0;
  }

  /**
   * Players used to steer directly to the same tactical point. When several
   * defenders were marking one threat, their icons occupied the same pixel.
   * A light deterministic separation force keeps football distances readable
   * without turning the pitch into billiards.
   */
  private separationVectors(state: MatchState, activeDuelPlayers: ReadonlySet<string>): Vector2[] {
    const forces = state.players.map(() => ({ x: 0, y: 0 }));
    for (let firstIndex = 0; firstIndex < state.players.length; firstIndex++) {
      const first = state.players[firstIndex];
      for (let secondIndex = firstIndex + 1; secondIndex < state.players.length; secondIndex++) {
        const second = state.players[secondIndex];
        const teammates = first.teamSide === second.teamSide;
        // The spacing has to be strong enough to win against two players
        // receiving the same marking/cover target.  Teammates need a clear
        // portrait-sized lane; opponents may be closer only when they are the
        // actual ball owner and designated presser.
        const activeDuel = this.isActiveDuelPair(activeDuelPlayers, first, second);
        const firstPreferred = teammates ? (first.positionType === 'GK' ? 4.8 : 6.8) : activeDuel ? 2.05 : 3.15;
        const secondPreferred = teammates ? (second.positionType === 'GK' ? 4.8 : 6.8) : activeDuel ? 2.05 : 3.15;
        const dx = first.position.x - second.position.x;
        const dy = first.position.y - second.position.y;
        const squaredGap = dx * dx + dy * dy;
        const maximumPreferred = Math.max(firstPreferred, secondPreferred);
        if (squaredGap >= maximumPreferred * maximumPreferred) continue;
        const gap = Math.sqrt(squaredGap);
        const fallback = gap > .001 ? undefined : this.overlapDirection(first.playerId, second.playerId);
        const directionX = fallback?.x ?? dx / gap;
        const directionY = fallback?.y ?? dy / gap;
        const teamFactor = teammates ? 1.38 : activeDuel ? .9 : 1.18;
        if (gap < firstPreferred) {
          const intensity = Math.pow(1 - gap / firstPreferred, 1.35) * teamFactor;
          forces[firstIndex].x += directionX * intensity;
          forces[firstIndex].y += directionY * intensity;
        }
        if (gap < secondPreferred) {
          const intensity = Math.pow(1 - gap / secondPreferred, 1.35) * teamFactor;
          forces[secondIndex].x -= directionX * intensity;
          forces[secondIndex].y -= directionY * intensity;
        }
      }
    }
    for (const force of forces) {
      const magnitude = Math.hypot(force.x, force.y);
      if (magnitude > 1.45) {
        force.x = force.x / magnitude * 1.45;
        force.y = force.y / magnitude * 1.45;
      }
    }
    return forces;
  }

  /**
   * Steering makes players avoid one another while they travel, but two
   * simultaneous marks can still reach the same point in a single fixed step.
   * Resolve only the remaining overlap afterwards so portraits never stack
   * into one unreadable icon, while keeping legitimate opponent duels close.
   */
  private resolveCrowding(state: MatchState, deltaTime: number, activeDuelPlayers: ReadonlySet<string>): void {
    // The correction stays single-pass and bounded: the persistent separation
    // steering resolves chains over subsequent 30 fps frames without adding a
    // costly second collision pass to every simulation step.
    for (let relaxation = 0; relaxation < 1; relaxation++) {
      for (let firstIndex = 0; firstIndex < state.players.length; firstIndex++) {
        const first = state.players[firstIndex];
        for (let secondIndex = firstIndex + 1; secondIndex < state.players.length; secondIndex++) {
          const second = state.players[secondIndex];
          const teammates = first.teamSide === second.teamSide;
          const desiredGap = teammates ? 5.25 : this.isActiveDuelPair(activeDuelPlayers, first, second) ? 1.55 : 2.45;
          const dx = first.position.x - second.position.x;
          const dy = first.position.y - second.position.y;
          const squaredGap = dx * dx + dy * dy;
          if (squaredGap >= desiredGap * desiredGap) continue;
          const gap = Math.sqrt(squaredGap);
          const fallback = gap > .001 ? undefined : this.overlapDirection(first.playerId, second.playerId);
          const direction = fallback ?? { x: dx / gap, y: dy / gap };
          // This correction runs after steering, but must still obey a movement
          // budget. Resolving a complete overlap in one 100 ms tick was the
          // source of the visible shaking/"teleport" when several players met
          // around a loose ball.
          const correction = Math.min(
            (desiredGap - gap) * (teammates ? .72 : .58),
            Math.max(.14, Math.min(1.9, deltaTime * 2.3)),
          );
          const firstShare = first.positionType === 'GK' ? .2 : second.positionType === 'GK' ? .8 : .5;
          first.position = clampPosition(add(first.position, scale(direction, correction * firstShare)));
          second.position = clampPosition(add(second.position, scale(direction, -correction * (1 - firstShare))));
          this.keepGoalkeeperInArea(first);
          this.keepGoalkeeperInArea(second);
        }
      }
    }
  }

  private overlapDirection(firstId: string, secondId: string): Vector2 {
    const first = [...firstId].reduce((value, character) => value + character.charCodeAt(0), 0);
    const second = [...secondId].reduce((value, character) => value + character.charCodeAt(0), 0);
    const angle = ((first - second + 720) % 360) * Math.PI / 180;
    return { x: Math.cos(angle), y: Math.sin(angle) };
  }

  /** Determine the one legal close-contact pair once per movement frame.
   * The old per-pair search made a full match unnecessarily expensive. */
  private activeDuelPlayers(state: MatchState): ReadonlySet<string> {
    const ownerId = state.ball.ownerPlayerId;
    const owner = ownerId ? state.players.find((player) => player.playerId === ownerId) : undefined;
    if (!owner || owner.positionType === 'GK') return new Set();
    const closest = state.players
      .filter((player) => player.teamSide !== owner.teamSide && player.positionType !== 'GK')
      .sort((a, b) => distance(a.position, owner.position) - distance(b.position, owner.position)
        || a.playerId.localeCompare(b.playerId))[0];
    return closest ? new Set([owner.playerId, closest.playerId]) : new Set();
  }

  /** Only the closest defender is allowed to occupy true tackle distance.
   * Everyone else keeps enough room to make the duel readable and to avoid a
   * pack repeatedly collapsing onto one tactical coordinate. */
  private isActiveDuelPair(activeDuelPlayers: ReadonlySet<string>, first: PlayerRuntimeState, second: PlayerRuntimeState): boolean {
    return first.teamSide !== second.teamSide
      && activeDuelPlayers.has(first.playerId)
      && activeDuelPlayers.has(second.playerId);
  }

  private keepGoalkeeperInArea(player: PlayerRuntimeState): void {
    if (player.positionType !== 'GK') return;
    player.position = PITCH_GEOMETRY.clampGoalkeeperPhysicalPosition(player.teamSide, player.position);
  }
}
